import React, { useEffect, useRef } from "react";
import { loadGoogleMaps } from "@/lib/maps";
import { trackOrg } from "@/lib/track";

const TEAL = "#14b8a6", RED = "#ef4444", AMBER = "#f59e0b";
const ring = (m: any) => (m.geojson.type === "LineString" ? m.geojson.coordinates : m.geojson.coordinates[0]);

/** Pure geometry outline (no imagery) — always available, even without a Maps key. */
export function GeometryPreview({ measurements }: { measurements: any[] }) {
  const pts = measurements.flatMap(ring);
  if (!pts.length) return null;
  const lat0 = pts.reduce((a: number, p: number[]) => a + p[1], 0) / pts.length;
  const k = Math.cos((lat0 * Math.PI) / 180);
  const xs = pts.map((p: number[]) => p[0] * k), ys = pts.map((p: number[]) => p[1]);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const span = Math.max(maxX - minX, maxY - minY) || 1;
  const tx = (p: number[]) => `${(((p[0] * k - minX) / span) * 280 + 10).toFixed(1)},${((1 - (p[1] - minY) / span) * 280 + 10 - (1 - (maxY - minY) / span) * 280).toFixed(1)}`;
  return (
    <svg viewBox="0 0 300 300" className="w-full max-w-sm bg-secondary/50 rounded-lg" data-testid="geometry-preview">
      {measurements.map((m) => m.geojson.type === "LineString" ? (
        <polyline key={m.id} points={m.geojson.coordinates.map(tx).join(" ")} fill="none" stroke={AMBER} strokeWidth={3} />
      ) : (
        <polygon key={m.id} points={m.geojson.coordinates[0].map(tx).join(" ")} fill={m.type === "exclude" ? RED : TEAL} fillOpacity={0.3} stroke={m.type === "exclude" ? RED : TEAL} strokeWidth={2} />
      ))}
    </svg>
  );
}

export function LeadMap({ measurements, anchor, maps, leadId }: { measurements: any[]; anchor: any; maps: any; leadId?: string }) {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(maps.browser_key).then(async (g) => {
      const { Map } = await g.maps.importLibrary("maps");
      if (cancelled || !el.current) return;
      const map = new Map(el.current, { center: anchor, zoom: 20, mapTypeId: "satellite", tilt: 0, disableDefaultUI: true, zoomControl: true });
      trackOrg("lead_map_load", leadId);
      const bounds = new g.maps.LatLngBounds();
      measurements.forEach((m) => {
        const path = ring(m).map(([lng, lat]: number[]) => ({ lat, lng }));
        path.forEach((p: any) => bounds.extend(p));
        if (m.geojson.type === "LineString") {
          new g.maps.Polyline({ map, path, strokeColor: AMBER, strokeWeight: 4, clickable: false });
          return;
        }
        const c = m.type === "exclude" ? RED : TEAL;
        new g.maps.Polygon({ map, paths: path, strokeColor: c, strokeWeight: 2, fillColor: c, fillOpacity: 0.3, clickable: false });
      });
      if (measurements.length) map.fitBounds(bounds, 40);
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [measurements, anchor, maps]);
  return <div ref={el} className="w-full h-[360px] rounded-lg bg-secondary" data-testid="lead-satellite-map" />;
}
